import { initMessageActions } from "./initMessageActions";
import { setupQuiz } from "./questionQuiz";
import { typingAnimationHTML } from "./typingAnimation";
import { scrollIfAllowed } from "./scrollIfAllowed";
import { messageQueue } from "./messageQueue";
import { hideMessageLoader } from "./hideMessageLoader";
import { mediaReady } from "./mediaReady";
import { initVideoLoaders } from "./initVideoLoaders";
import { initMessageLoader } from "./initMessageLoader";
import { autoPlayMessage } from "./playMessage";
import { chatDebugLog } from "../helpers/chatDebug";

/**
 * Cancellation flag — when set to true, the one-by-one chain stops
 * and no further history messages are revealed.
 */
let _cancelled = false;

/**
 * Call this from outside (e.g. submitMessage) to stop
 * the one-by-one loading immediately.
 */
export function cancelLoadMessagesOneByOne() {
  _cancelled = true;
}

export function loadMessagesOneByOne(messages, options = {}) {
  const chatContent = messages.closest(".g-chatform-content");
  const chatForm = messages.closest(".g-chatform");
  const autoPlayEnabled =
    options.autoPlay ||
    chatForm.find('input[name="automatic_voice_messages"]').is(":checked");

  _cancelled = false;

  chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:start", {
    messageCount: messages.length,
    autoPlayEnabled,
  });

  messages.hide();

  // Process messages sequentially using promise chain
  return messageQueue.addToQueue(() => {
    let messageChain = Promise.resolve();

    function revealActionsAndContinue(actions, delay, resolve, debugLabel, debugData = {}) {
      if (!actions || actions.length === 0) {
        chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:no-actions", {
          debugLabel,
          ...debugData,
        });
        setTimeout(resolve, delay);
        return;
      }

      actions.fadeIn(200, () => {
        chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:actions-visible", {
          debugLabel,
          ...debugData,
        });
        setTimeout(resolve, delay);
      });
    }

    messages.each(function (index, element) {
      const message = $(element);
      const contentHolder = message.find(".g-chatform-message-inner");
      const content = message.find(".g-chatform-message-content-inner");
      const actions = message.find(".g-chatform-message-actions");

      // Inject raw text NOW before typing animations can clear the DOM.
      // Clone so hidden display:none elements are still readable; strip quiz/UI markup.
      const contentClone = content.clone();
      contentClone
        .find(".growtype-quiz-wrapper, .g-chatform-message-actions")
        .remove();
      const rawText = contentClone.text().replace(/\s+/g, " ").trim();
      if (rawText) message.attr("data-raw-text", rawText);

      chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:prepare-message", {
        index,
        role: message.attr("data-role"),
        messageType: message.attr("data-message-type"),
        hasRawText: Boolean(rawText),
        rawTextPreview: rawText.slice(0, 120),
        hasQuizChips: content.find(".g-chat-quiz-chips").length > 0,
        contentHolderStyle: contentHolder.attr("style") || "",
        contentStyle: content.attr("style") || "",
      });

      content.hide();
      contentHolder.hide();
      actions.hide();

      // Chain this message to wait for previous message to complete
      messageChain = messageChain.then(async () => {
        // If cancelled, resolve immediately and skip this message
        if (_cancelled) {
          chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:cancelled", { index });
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          // Show message and its loader only when it's this message's turn
          const hasMedia =
            message.find(
              ".g-chatform-message-assets-wrapper, .g-chatform-message-img-wrapper, .audio-message, video, .gift-wrapper"
            ).length > 0;

          if (hasMedia) {
            // For media messages, let initVideoLoaders manage the buffering and reveal state
            initVideoLoaders(message, "dots", true);
          } else {
            // For text messages, show the typing loader and prepare for typing animation
            initMessageLoader(message);
            initVideoLoaders(message, "dots", false);
          }

          chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:show-message", {
            index,
            hasMedia,
            role: message.attr("data-role"),
            holderDisplayBeforeShow: contentHolder[0]?.style?.display || "",
            contentDisplayBeforeShow: content[0]?.style?.display || "",
          });

          message.show();

          // Calculate human-like thinking delay with natural variation
          const messageText = message
            .find(".g-chatform-message-content-inner")
            .text()
            .trim();
          let dynamicDelay = 0;

          if (index !== 0) {
            dynamicDelay = 400 + Math.floor(Math.random() * 1000); // Base 400-1400ms

            if (messageText.length > 50)
              dynamicDelay += 500 + Math.floor(Math.random() * 1200);
            if (messageText.length > 150)
              dynamicDelay += 1000 + Math.floor(Math.random() * 2000);

            // Occasional longer pause (20% chance) to feel more natural
            if (Math.random() < 0.2) {
              dynamicDelay += 800 + Math.floor(Math.random() * 1200);
            }
          }

          const thinkingDelay = new Promise((r) => setTimeout(r, dynamicDelay));

          Promise.all([mediaReady(message), thinkingDelay]).then(() => {
            chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:ready", {
              index,
              role: message.attr("data-role"),
              hasMedia,
              contentHolderDisplay: contentHolder[0]?.style?.display || "",
              contentDisplay: content[0]?.style?.display || "",
            });

            // Initialize actions (quiz, play, etc.) for this message
            initMessageActions(message);
            // Initialize quiz handlers if any quiz present
            const quizWrappers = message.find(".growtype-quiz-wrapper");
            quizWrappers.each(function () {
              setupQuiz($(this));
            });

            const relativeTop = message.position().top;
            const autoEnabled = chatContent[0].isAutoScrollEnabled !== false;
            const userInteracting = chatContent[0].isUserInteracting === true;

            if (autoEnabled && !userInteracting) {
              chatContent.animate({ scrollTop: relativeTop - 20 }, 400);
            }

            const isUser = message.attr("data-role") === "user";

            const finish = () => {
              chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:finish", {
                index,
                isUser,
                hasMedia,
                holderDisplayBefore: contentHolder[0]?.style?.display || "",
                contentDisplayBefore: content[0]?.style?.display || "",
              });

              hideMessageLoader(message);
              contentHolder.show();
              const hasMedia =
                message.find(
                  ".g-chatform-message-assets-wrapper, .g-chatform-message-img-wrapper, .audio-message, video, .gift-wrapper"
                ).length > 0;

              if (isUser && !hasMedia) {
                // For bot messages with no media, apply typing animation (it handles showing content)
                if (autoEnabled && !userInteracting) {
                  // Pre-scroll to create space for typing animation
                  const scrollHeight = chatContent[0].scrollHeight;
                  const containerHeight = chatContent.height();
                  chatContent.scrollTop(scrollHeight - containerHeight + 100);

                  // Also call the helper to be sure
                  scrollIfAllowed();
                }

                // Fire TTS immediately as typing starts — fully fire-and-forget, never blocks.
                if (autoPlayEnabled) {
                  autoPlayMessage(message);
                }

                // Apply typing animation concurrently with TTS
                typingAnimationHTML(message).then(() => {
                  chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:typing-complete", {
                    index,
                    holderDisplayAfter: contentHolder[0]?.style?.display || "",
                    contentDisplayAfter: content[0]?.style?.display || "",
                  });

                  // Dispatch event that typing animation is complete
                  document.dispatchEvent(
                    new CustomEvent("typingAnimationComplete", {
                      detail: {
                        message: message[0],
                        messageIndex: index,
                      },
                    })
                  );

                  // After typing completes, show actions and immediately resolve to next message.
                  // Audio continues playing independently via audioPlaybackQueue.
                  revealActionsAndContinue(actions, 300, resolve, "typing-complete", {
                    index,
                  });
                });
              } else {
                // For messages with media or admin messages, just fade in normally
                content.fadeIn(200, () => {
                  chatDebugLog('chatContent', "[chat-debug] loadMessagesOneByOne:fadein-complete", {
                    index,
                    holderDisplayAfter: contentHolder[0]?.style?.display || "",
                    contentDisplayAfter: content[0]?.style?.display || "",
                  });
                  revealActionsAndContinue(actions, 500, () => {
                    scrollIfAllowed();
                    resolve();
                  }, "fadein-complete", {
                    index,
                  });
                });
              }
            };

            // For historical messages, show content immediately without loader/typing
            finish();
          });
        });
      });
    });

    return messageChain;
  });
}
